import Phaser from 'phaser';

/**
 * Reserved bottom-right panel for future ability buttons. Empty
 * placeholder slots render on load so the layout is stable from day
 * one. Tapping a slot is a no-op until abilities are wired.
 *
 * Mobile-sized (56px targets, 12px gap) to meet touch accessibility.
 */
export interface ActionBarSlot {
  readonly index: number;
  readonly hotkey: string;
  setIcon(char: string): void;
  setEnabled(enabled: boolean): void;
  onActivate(handler: () => void): void;
}

const SLOT_SIZE = 56;
const SLOT_GAP = 12;
const SLOT_COUNT = 4;
const MARGIN = 24;

export class ActionBar {
  private slots: InternalSlot[] = [];

  constructor(private readonly scene: Phaser.Scene) {
    const { width, height } = scene.scale;
    const totalW = SLOT_COUNT * SLOT_SIZE + (SLOT_COUNT - 1) * SLOT_GAP;
    const startX = width - MARGIN - totalW + SLOT_SIZE / 2;
    const y = height - MARGIN - SLOT_SIZE / 2;

    const hotkeys = ['1', '2', '3', '4'];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const x = startX + i * (SLOT_SIZE + SLOT_GAP);
      this.slots.push(new InternalSlot(scene, x, y, i, hotkeys[i]));
    }
  }

  get(index: number): ActionBarSlot | undefined {
    return this.slots[index];
  }

  destroy(): void {
    for (const s of this.slots) s.destroy();
    this.slots = [];
  }
}

class InternalSlot implements ActionBarSlot {
  private bg: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private iconText: Phaser.GameObjects.Text;
  private hotkeyText: Phaser.GameObjects.Text;
  private hitZone: Phaser.GameObjects.Rectangle;
  private enabled = false;
  private handler: (() => void) | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    readonly index: number,
    readonly hotkey: string,
  ) {
    this.bg = scene.add
      .rectangle(x, y, SLOT_SIZE, SLOT_SIZE, 0x0a0a0a, 0.7)
      .setStrokeStyle(1, 0x2a2a2a, 0.8)
      .setScrollFactor(0, 0)
      .setDepth(1000);

    this.border = scene.add
      .rectangle(x, y, SLOT_SIZE + 2, SLOT_SIZE + 2, 0x000000, 0)
      .setStrokeStyle(2, 0x6cff6c, 0)
      .setScrollFactor(0, 0)
      .setDepth(1001);

    this.iconText = scene.add
      .text(x, y, '·', {
        fontSize: '24px',
        color: '#454545',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5)
      .setScrollFactor(0, 0)
      .setDepth(1002);

    this.hotkeyText = scene.add
      .text(x + SLOT_SIZE / 2 - 8, y + SLOT_SIZE / 2 - 10, hotkey, {
        fontSize: '11px',
        color: '#555555',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5)
      .setScrollFactor(0, 0)
      .setDepth(1002);

    this.hitZone = scene.add
      .rectangle(x, y, SLOT_SIZE + 8, SLOT_SIZE + 8, 0x000000, 0)
      .setScrollFactor(0, 0)
      .setDepth(1003)
      .setInteractive({ useHandCursor: true });

    this.hitZone.on('pointerdown', () => {
      if (!this.enabled || !this.handler) return;
      this.handler();
      scene.tweens.add({
        targets: this.border,
        alpha: { from: 1, to: 0 },
        duration: 300,
      });
    });
  }

  setIcon(char: string): void {
    this.iconText.setText(char);
    if (this.enabled) this.iconText.setColor('#e0ffe0');
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.iconText.setColor('#e0ffe0');
      this.hotkeyText.setColor('#b0ffb0');
      this.bg.setFillStyle(0x1a2a1a, 0.85);
      this.border.setStrokeStyle(2, 0x6cff6c, 1);
    } else {
      this.iconText.setColor('#454545');
      this.hotkeyText.setColor('#555555');
      this.bg.setFillStyle(0x0a0a0a, 0.7);
      this.border.setStrokeStyle(1, 0x2a2a2a, 0.8);
    }
  }

  onActivate(handler: () => void): void {
    this.handler = handler;
  }

  destroy(): void {
    this.bg.destroy();
    this.border.destroy();
    this.iconText.destroy();
    this.hotkeyText.destroy();
    this.hitZone.destroy();
  }
}
